import React, { useContext, useEffect, useState, useRef } from "react";
import "./teamPage.css";
import { CombinedContext } from "../../context/combinedContext";
import generalRequest from "../../request/generalRequest";
import companyBasedRequest from "../../request/companyBasedRequest";
import { UserPlus, Edit, Trash2, Rocket, Camera } from "lucide-react";
import translations from "../../translations/translations";
import TeamServicesModal from "./staffServicesModal/teamServicesModal";
import ProfileUploadModal from "./profilePhotoModal/profilePhotoModal";
import StaffInfoModal from "./staffInfoModal/staffInfoModal";
import TeamPageSkeleton from "./teamPageSkeleton/teamPageSkeleton";
import TeamModal from "./teamModal/teamModal";
import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";

const TeamPage = () => {
	const { userData, language } = useContext(CombinedContext);

	const [shopData, setShopData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [selectedStaffProfile, setSelectedStaffProfile] = useState(null);
	const [teamProfiles, setTeamProfiles] = useState([]);
	const [loadingTeam, setLoadingTeam] = useState(true);
	const [activeModal, setActiveModal] = useState(null);
	const [msg, setMsg] = useState(null);
	const [activeServiceCategory, setActiveServiceCategory] = useState(null);
	const [banner, setBanner] = useState({
		text: "",
		type: "info",
		exiting: false,
	});

	const [exiting, setExiting] = useState(false);

	const serviceCategoryRefs = useRef({});

	const showBanner = (type, text, duration = 4000) => {
		setBanner({ type, text });
		setExiting(false);

		setTimeout(() => {
			setExiting(true);
		}, duration - 250);

		setTimeout(() => {
			setBanner(null);
			setExiting(false);
		}, duration);
	};

	const showBannerExtended = (type, text, duration = 10000) => {
		setBanner({ type, text });
		setExiting(false);

		setTimeout(() => {
			setExiting(true);
		}, duration - 250);

		setTimeout(() => {
			setBanner(null);
			setExiting(false);
		}, duration);
	};


	useEffect(() => {
		if (!categoriesWithServices.length) return;

		setActiveServiceCategory(categoriesWithServices[0].category.categoryId);
	}, [selectedStaffProfile]);

	useEffect(() => {
		async function load() {
			try {
				const res = await companyBasedRequest.getShopWithstaffsByIdPrivate(userData.user.shopId, userData.cognitoTokens.AccessToken);

				setShopData(res.shop);
			} catch (err) {
				console.error("Error loading team:", err);
			} finally {
				setLoading(false);
			}
		}
		if (userData?.user?.shopId) load();
	}, [userData]);

	useEffect(() => {
		if (!shopData || !userData?.user?.userId) return;

		// Find the staff object for the logged-in user
		const selfStaff = shopData.staffs.find((st) => st.userId === userData.user.userId);

		if (selfStaff) {
			loadStaffPrivateInfo(selfStaff);
		}
	}, [shopData, userData]);

	const handleLaunchStaff = async (staffId) => {
		try {
			setMsg(null);
			const res = await companyBasedRequest.launchStaff(staffId, userData.cognitoTokens.AccessToken);

			setMsg({ type: "success", text: res.message });

			const updated = await companyBasedRequest.getShopWithstaffsByIdPrivate(userData.user.shopId, userData.cognitoTokens.AccessToken);
			setShopData(updated.shop);
		} catch {
			setMsg({ type: "error", text: "Failed to activate staff profile." });
		}
	};

	const handleRemoveStaff = async (staff) => {
		if (!window.confirm("Remove this staff member?")) return;

		try {
			await companyBasedRequest.removeStaff(shopData.shopId, staff.userId, staff.email);

			const updated = await companyBasedRequest.getShopWithstaffsByIdPrivate(userData.user.shopId, userData.cognitoTokens.AccessToken);

			setShopData(updated.shop);
			setSelectedStaffProfile(null);
		} catch (err) {
			console.error("Remove error:", err);
		}
	};

	const loadStaffPrivateInfo = async (staff) => {
		if (selectedStaffProfile?.staffId === staff.userId) {
			return;
		}

		if (!staff.staffId && staff.status === "invited") {
			setSelectedStaffProfile(null);
			return;
		}

		try {
			const res = await companyBasedRequest.getStaffInfoPrivate(staff.userId, userData.cognitoTokens.AccessToken);
			setSelectedStaffProfile(res.staff);
		} catch (err) {
			console.error("Failed to load staff info:", err);
		}
	};

	const staffServiceIds = Array.isArray(selectedStaffProfile?.services)
		? selectedStaffProfile.services.map((s) => (typeof s === "string" ? s : s.serviceId)).filter(Boolean)
		: [];

	const staffFullServices = (shopData?.services || []).filter((svc) => staffServiceIds.includes(svc.serviceId));

	const servicesByCategory = (shopData?.serviceCategories || []).map((cat) => ({
		category: cat,
		services: staffFullServices.filter((s) => s.categoryId === cat.categoryId),
	}));

	const categoriesWithServices = servicesByCategory.filter((x) => x.services.length > 0);

	if (loading || !shopData) return <TeamPageSkeleton />;

	function formatPhone(number) {
		if (!number) return "";
		const digits = number.replace(/\D/g, "");

		if (digits.length === 11 && digits.startsWith("1")) {
			return "+1 " + "(" + digits.slice(1, 4) + ") " + digits.slice(4, 7) + "-" + digits.slice(7);
		}

		return number.startsWith("+") ? number : "+" + number;
	}

	return (
		<div className="sf-team-page">
			{/* HEADER */}
			<div className="sf-team-header">
				<h1 className="sf-team-title">{translations[language].teamPage.header.title}</h1>

				<button className="sf-team-invite-btn" onClick={() => setActiveModal("team")}>
					<UserPlus size={18} />
					{translations[language].teamPage.header.manageStaff}
				</button>
			</div>

			{msg && <div className={`sf-team-msg ${msg.type}`}>{msg.text}</div>}

			{/* STAFF LIST */}
			<div className="sf-team-grid">
				{shopData.staffs.map((staff) => {
					const isJoined = staff.status === "joined" || staff.status === "active";
					const isPending = staff.onboarding?.isComplete && staff.status !== "active";

					return (
						<div
							key={staff.staffId || staff.email}
							className={`sf-team-card ${isJoined ? "clickable" : "disabled"}`}
							onClick={() => isJoined && loadStaffPrivateInfo(staff)}
						>
							{/* Avatar */}
							<div className="sf-team-avatar">
								{staff.photo ? (
									<img src={staff.photo} alt={staff.firstName} />
								) : (
									<div className="sf-team-avatar-fallback">{staff.firstName?.charAt(0).toUpperCase() || "U"}</div>
								)}
							</div>

							{/* Info */}
							<div className="sf-team-info">
								<div className="sf-team-name">
									{staff.firstName} {staff.lastName}
								</div>
								<div className="sf-team-email">{staff.email}</div>

								<div className={`sf-team-status ${staff.status}`}>{translations[language].teamPage.status[staff.status] || staff.status}</div>
							</div>

							{/* Actions */}
							<div className="sf-team-actions">
								{isPending && (
									<button
										className="sf-team-launch-btn"
										onClick={(e) => {
											e.stopPropagation();
											handleLaunchStaff(staff.staffId);
										}}
									>
										<Rocket size={16} />
										{translations[language].teamPage.main.launch}
									</button>
								)}
							</div>
						</div>
					);
				})}
			</div>

			{selectedStaffProfile && (
				<div className="sf-teamview-container">
					{/* HEADER */}
					<div className="sf-teamview-header">
						{/* Avatar */}
						<div className="sf-teamview-avatar-wrapper">
							{selectedStaffProfile.photo ? (
								<img src={selectedStaffProfile.photo} alt={selectedStaffProfile.fullName} className="sf-teamview-avatar" />
							) : (
								<div className="sf-teamview-avatar-fallback">{selectedStaffProfile.firstName?.charAt(0).toUpperCase()}</div>
							)}

							<button className="sf-teamview-avatar-edit-btn" onClick={() => setActiveModal("avatar")}>
								<Camera size={16} />
							</button>
						</div>

						{/* Name + bio */}
						<div className="sf-teamview-info">
							<div className="sf-teamview-info-header">
								<h2 className="sf-teamview-name">{selectedStaffProfile.fullName}</h2>

								<button className="sf-teamview-edit-btn" onClick={() => setActiveModal("info")}>
									<Edit size={16} />
								</button>
							</div>

							{selectedStaffProfile.bio && <p className="sf-teamview-bio">{selectedStaffProfile.bio}</p>}

							<div className="sf-teamview-contact">
								<div>
									<strong>{translations[language].teamPage.labels.email}</strong> {selectedStaffProfile.email}
								</div>
								<div>
									<strong>{translations[language].teamPage.labels.phone}</strong> {formatPhone(selectedStaffProfile.phone)}
								</div>
							</div>
						</div>
					</div>

					{/* SERVICES */}
					<div className="sf-teamview-services">
						<div className="sf-teamview-services-header">
							<h3 className="sf-teamview-section-title">{translations[language].teamPage.main.services}</h3>

							<button className="sf-teamview-edit-btn" onClick={() => setActiveModal("services")}>
								<Edit size={14} />
							</button>
						</div>

						{selectedStaffProfile.services.length === 0 && (
							<div className="sf-teamview-empty">{translations[language].teamPage.main.noServices}</div>
						)}

						<div className="sf-teamview-services">
							{categoriesWithServices.length === 0 && <div className="sf-teamview-empty">{translations[language].teamPage.main.noServices}</div>}
							{categoriesWithServices.length > 0 && (
								<div className="sf-teamview-tabs sf-teamview-tabs--sticky">
									{categoriesWithServices.map(({ category }) => (
										<button
											key={category.categoryId}
											type="button"
											className={`sf-teamview-tab-btn ${activeServiceCategory === category.categoryId ? "active" : ""}`}
											onClick={() => {
												setActiveServiceCategory(category.categoryId);

												serviceCategoryRefs.current[category.categoryId]?.scrollIntoView({
													behavior: "smooth",
													block: "start",
												});
											}}
										>
											{category.name?.[language]}
										</button>
									))}
								</div>
							)}

							{categoriesWithServices.map(({ category, services }) => (
								<div
									key={category.categoryId}
									ref={(el) => (serviceCategoryRefs.current[category.categoryId] = el)}
									className="sf-teamview-category-section"
								>
									<h4 className="sf-teamview-category-title">{category.name?.[language]}</h4>

									{services.length > 0 ? (
										services.map((srv) => (
											<div className="sf-teamview-service-item" key={srv.serviceId}>
												<div className="sf-teamview-service-left">
													<div className="sf-teamview-service-name">
														{srv.name}
														{srv.isHomeService && (
															<span className="sf-teamview-service-home">
																{" · "}
																{language === "es" ? "A domicilio" : "Home service"}
															</span>
														)}
													</div>
													{srv.description && <div className="sf-teamview-service-desc">{srv.description}</div>}
												</div>

												<div className="sf-teamview-service-right">
													<div className="sf-teamview-service-price">
														{srv.currency} {srv.price}
													</div>
													<div className="sf-teamview-service-duration">{srv.duration} min</div>
												</div>
											</div>
										))
									) : (
										<div className="sf-teamview-empty-category">
											{language === "es"
												? "Este miembro no ofrece servicios en esta categoría"
												: "This staff member has no services in this category"}
										</div>
									)}
								</div>
							))}
						</div>
					</div>
				</div>
			)}

			{banner && <BannerMessageFixed type={banner.type} text={banner.text} />}

			{activeModal === "services" && (
				<TeamServicesModal
					isOpen
					onClose={() => setActiveModal(null)}
					shopServices={shopData.services}
					staffProfile={selectedStaffProfile}
					onSave={(newServiceIds) => {
						const fullServices = shopData.services.filter((svc) => newServiceIds.includes(svc.serviceId));
						setSelectedStaffProfile((prev) => ({
							...prev,
							services: fullServices,
						}));
					}}
				/>
			)}

			{activeModal === "team" && (
				<TeamModal
					open={true}
					onClose={() => setActiveModal(null)}
					shopData={shopData}
					onStaffAdded={(newStaff) => {
						setShopData((prev) => ({
							...prev,
							staffs: [...prev.staffs, ...newStaff],
						}));
					}}
					onStaffRemoved={(email) => {
						setShopData((prev) => ({
							...prev,
							staffs: prev.staffs.filter((s) => s.email !== email),
						}));
					}}
					onRoleUpdated={(userId, newRole) => {
						setShopData((prev) => ({
							...prev,
							staffs: prev.staffs.map((s) => (s.userId === userId ? { ...s, role: newRole } : s)),
						}));
					}}
					onInviteSent={() => showBannerExtended("success", translations[language].teamPage.toast.inviteSent)}
				/>
			)}
			{activeModal === "services" && <TeamServicesModal open={true} onClose={() => setActiveModal(null)} shopData={shopData} />}
			{activeModal === "avatar" && (
				<ProfileUploadModal
					isOpen
					staffProfile={selectedStaffProfile}
					onClose={() => setActiveModal(null)}
					onUploaded={(newUrl) => {
						const bustedUrl = `${newUrl}?t=${Date.now()}`;

						setSelectedStaffProfile((prev) => ({
							...prev,
							photo: bustedUrl,
						}));

						setShopData((prev) => {
							const updatedStaffs = prev.staffs.map((s) => (s.userId === selectedStaffProfile.userId ? { ...s, photo: bustedUrl } : s));

							return { ...prev, staffs: updatedStaffs };
						});
					}}
				/>
			)}
			{activeModal === "info" && (
				<StaffInfoModal
					open
					staffProfile={selectedStaffProfile}
					onClose={() => setActiveModal(null)}
					onSave={(updated) => {
						// Update detail panel instantly
						setSelectedStaffProfile((prev) => ({
							...prev,
							...updated,
						}));

						// Update list
						setShopData((prev) => {
							const updatedStaffs = prev.staffs.map((s) => (s.userId === selectedStaffProfile.userId ? { ...s, ...updated } : s));
							return { ...prev, staffs: updatedStaffs };
						});
					}}
				/>
			)}
		</div>
	);
};

export default TeamPage;
